import { useAsyncExecutor2 } from "async-executor-hook";
import { addDays, differenceInDays, format } from "date-fns";
import { stringify } from "query-string";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormControl,
} from "rectangular-forms";

import { Authorization } from "../../../common/AuthorizationCpt";
import {
  inputMaxLengthFn,
  onlyDigits,
  WCalendar,
  WDropdown,
  WInputText,
  WInputTextDamPrefix,
  WMultipleTagInput,
  WMultipleTagInputAlphaNum,
} from "../../../common/components/TemplateContainer/InputTemplates";
import { ButtonReload } from "../../../common/components/TemplateContainer/InputTemplateStyles";
import { SaveButton } from "../../../common/components/TemplateContainer/SaveButton";
import { TemplateInputContainer } from "../../../common/components/TemplateContainer/TemplateContainer";
import { removeNullProps } from "../../../common/utils";
import IconVectorRightBlue from "../../../images/icon_button_users_blue.svg";
import { DAM_MAX_LENGTH } from "../../../utils/valueStaticConstants";
import { SearchBar } from "../FilterAdminStyles";

import Reload from "../../../images/ic_reload.svg";
import { PaymentPaidAdminParams$ } from "../interfaces/PaidBackoffice.interface";
import { DATE_SELECT } from "../../../utils/constants";

const REGIMEN_OPTION = [{ value: "10", label: "10 Imp. Definitiva" }];

export const PaymentPaidAdminSearch = (props: PaymentPaidAdminParams$) => {
  const { params$ } = props;

  const history = useHistory();

  const formConfig = useFormConfig({
    createForm: (data) => {
      const form = new FormGroup({
        company_info: new FormControl(),
        volante_nums: new FormControl(),
        from_date: new FormControl(),
        to_date: new FormControl(),
        year: new FormControl(),
        num: new FormControl(null, Validators.minLength(6)),
        regimen: new FormControl(),
        id_pedidos: new FormControl(),
        tci_nums: new FormControl(),
      });
      form.get("from_date").valueChanges.subscribe((from_date: Date) => {
        const toDate = new Date(form.get("to_date").value) || new Date();
        const diffDate = differenceInDays(toDate, new Date(from_date));
        if (diffDate < 1) {
          form.get("to_date").setValue(from_date);
        }
      });

      form.patchValue(data);
      return form;
    },
    onSubmit: (form) => {
      const data = form.value;
      const from_date = data.from_date && format(data.from_date, "yyyy-MM-dd");
      const to_date = data.to_date && format(data.to_date, "yyyy-MM-dd");
      const pathname = history.location.pathname;
      history.push(
        `${pathname}?${stringify(
          removeNullProps({ ...data, from_date, to_date })
        )}`
      );
    },
  });

  useAsyncExecutor2((params) => params, {
    params$,
    onSuccess: (params) => {
      formConfig.loadSucceed(params);
    },
  });

  const [damYearOptions] = useState(() => {
    return DATE_SELECT;
  });

  const damYear = formConfig.form?.get("year").value;
  const damRegimen = formConfig.form?.get("regimen").value;

  return (
    <SearchBar flexStart={false}>
      <WForm formConfig={formConfig} style={{ width: "100%" }}>
        <div
          className="p-grid"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div className="p-col-6 p-md-3">
            <WFormControl name="company_info">
              <TemplateInputContainer label="Agente Aduanero:">
                <WInputText
                  width="100%"
                  placeholder="Ingresa Razón social o el RUC"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-3">
            <WFormControl name="id_pedidos">
              <TemplateInputContainer label="Pedido(s):">
                <WMultipleTagInputAlphaNum height="5rem" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-3">
            <WFormControl name="volante_nums">
              <TemplateInputContainer label="Volante(s):">
                <WMultipleTagInput height="5rem" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-3">
            <WFormControl name="tci_nums">
              <TemplateInputContainer label="Números de serie factura(s):">
                <WMultipleTagInputAlphaNum height="5rem" alphaNum numInvoice />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1rem",
          }}
        >
          <div className="p-col-6 p-md-1">
            <WFormControl name="year">
              <TemplateInputContainer label="Año:">
                <WDropdown width="100%" options={damYearOptions} />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl name="regimen">
              <TemplateInputContainer label="Régimen:" color="#08082B">
                <WDropdown width="100%" options={REGIMEN_OPTION} />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl
              name="num"
              errorMessages={{
                minlength: "La dam debe de tener 6 dígitos",
              }}
            >
              <TemplateInputContainer label="Número DAM:" color="#08082B">
                <WInputTextDamPrefix
                  width="100%"
                  placeholder="123456"
                  damYear={damYear}
                  damRegimenId={damRegimen}
                  maxLength={DAM_MAX_LENGTH}
                  onInput={inputMaxLengthFn}
                  onKeyPress={onlyDigits}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl name="from_date">
              <TemplateInputContainer label="Retiro desde:" color="#08082B">
                <WCalendar width="100%" dateFormat="dd/mm/yy" />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div className="p-col-6 p-md-2">
            <WFormControl name="to_date">
              <TemplateInputContainer label="Retiro hasta:">
                <WCalendar
                  width="100%"
                  minDate={
                    formConfig.form?.get("from_date")?.value || new Date()
                  }
                  dateFormat="dd/mm/yy"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-col-6 p-md-1"
            style={{ display: "flex", alignItems: "center" }}
          >
            <ButtonReload
              type="button"
              height="5rem"
              backgroundColor="#002E6C"
              marginTop="3rem"
              onClick={() => {
                const form = formConfig.form;
                form.reset({
                  from_date: new Date(),
                  to_date: addDays(new Date(), 5),
                  regimen: "10",
                  year: new Date().getFullYear().toString(),
                  num: "",
                });
              }}
            >
              <img src={Reload} alt="reload" />
            </ButtonReload>
          </div>
          <div className="p-col-6 p-md-1">
            <div style={{ marginBottom: "1.8rem", height: "1.4rem" }} />
            <Authorization
              actions={["quotation.get_quotation_paid_backoffice"]}
            >
              <SaveButton
                width="100%"
                height="5rem"
                backgroundColor="#002E6C"
                iconRight={IconVectorRightBlue}
              />
            </Authorization>
          </div>
        </div>
        <div
          className="p-grid"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div className="p-col-6 p-md-5" />
        </div>
      </WForm>
    </SearchBar>
  );
};
